// components/PercentageTaxFormManagement.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Download, Trash2 } from "lucide-react";
import PercentageTaxForm from "./PercentageTaxForm";
import PercentageTaxList from "./PercentageTaxList";
import PercentageTaxSummary from "./PercentageTaxSummary"; // optional (kept)
import PercentageTaxEditModal from "./PercentageTaxEditModal";
import PercentageTaxQuarterSummary from "./PercentageTaxQuarterSummary";
import Button from "./Button";
import FilingCard from "./FilingCard";
import ConfirmModal from "./ConfirmModal";

import Form2306Form from "./Form2306Form";
import Form2307RecordsTable from "./Form2307RecordsTable";

import percentageTaxAPI from "../api/percentageTaxAPI";
import formAPI from "../api/formAPI";
import form2307API from "../api/form2307API";
import axiosInstance from "../api/axiosConfig";
import notify from "../lib/notify";
import { cleanTIN, findMissingTINs, missingTINMessage } from "../utils/tinFormat";
import { resolveFtypeCode, missingFtypeMessage } from "../utils/sawtFormType";
import { filerRdoCode, missingRdoMessage } from "../utils/rdoCode";
import { isCorporatePayee } from "../utils/payeeEntity";
import { isPercentageTaxWithholdingAtc, findMissingATCs, missingATCMessage } from "../utils/withholdingAtc";
import { buildSawtDat, downloadSawtDat } from "../utils/sawtDat";
import { datFilerBranch } from '../utils/branchCode';
import { formatCurrency } from "../utils/numberUtils";
import { recordAttachmentExport, ATTACHMENT_TYPES } from "../utils/recordAttachmentExport";

const MONTH_OPTIONS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

// Same shape as VATManagement's QUARTER_MONTHS — restricts the Month filter
// to the 3 months of whichever quarter is currently selected.
const QUARTER_MONTHS = {
  1: [{ value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' }],
  2: [{ value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' }],
  3: [{ value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' }],
  4: [{ value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }],
};

const calculateQuarterFromMonth = (month1to12) => {
  const m = Number(month1to12) || 1;
  return Math.floor((m - 1) / 3) + 1;
};

const PercentageTaxFormManagement = ({ client }) => {
  const [activeSection, setActiveSection] = useState("sales"); // start on sales tab

  // percentage tax
  const [percentageTaxRecords, setPercentageTaxRecords] = useState([]);
  const [percentageTaxSummaryRemote, setPercentageTaxSummaryRemote] = useState(null);

  // 2306
  const [form2306Records, setForm2306Records] = useState([]);

  // 2307. Read-only here: these are derived automatically when a transaction
  // carrying an ATC is posted, so this module displays them rather than
  // encoding them. Shown alongside the 2306s so a preparer sees every
  // certificate for the period in one place.
  const [form2307Records, setForm2307Records] = useState([]);
  const [loading2307, setLoading2307] = useState(false);
  const [excessFromPrevQuarter, setExcessFromPrevQuarter] = useState(0);

  const [loading, setLoading] = useState(false);

  // Period (Q + FY; month optional only for 2306 form POST usage)
  const [selectedPeriod, setSelectedPeriod] = useState({
    quarter: null,
    year: null,
    month: null,
  });

  // edit modal
  const [editingRecord, setEditingRecord] = useState(null);
  const openEditModal = (record) => setEditingRecord(record);
  const closeEditModal = () => setEditingRecord(null);

  const quarters = useMemo(
    () => [
      { value: 1, label: "Quarter 1 (Jan-Mar)" },
      { value: 2, label: "Quarter 2 (Apr-Jun)" },
      { value: 3, label: "Quarter 3 (Jul-Sep)" },
      { value: 4, label: "Quarter 4 (Oct-Dec)" },
    ],
    []
  );

  // Generate available years (current year and past 5 years)
  const getAvailableYears = useCallback(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => currentYear - i);
  }, []);

  /* ============================
      FETCH: Percentage Tax
  ============================ */
  const fetchPercentageTaxRecords = useCallback(async () => {
    if (!client?._id) return;
    //', client._id, 'activeSection:', activeSection);

    try {
      setLoading(true);
      // determine category filter from current section
      const categoryFilter =
        activeSection === 'sales'
          ? 'sales'
          : activeSection === 'income'
          ? 'income'
          : undefined;
      const params = {};
      if (categoryFilter) params.category = categoryFilter;

      const response = await percentageTaxAPI.getByClient(client._id, params);

      // Your backend returns { success:true, data:{records,...}}
      const records =
        response?.data?.data?.records ||
        response?.data?.records ||
        (Array.isArray(response?.data) ? response.data : []);

      setPercentageTaxRecords(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Error fetching Percentage Tax records:", error);
      setPercentageTaxRecords([]);
    } finally {
      setLoading(false);
    }
  }, [client?._id, activeSection]);

  const fetchPercentageTaxSummary = useCallback(async () => {
    if (!client?._id) return;

    try {
      const categoryFilter =
        activeSection === 'sales'
          ? 'sales'
          : activeSection === 'income'
          ? 'income'
          : undefined;
      const params = {};
      if (categoryFilter) params.category = categoryFilter;

      const response = await percentageTaxAPI.getSummary(client._id, params);
      setPercentageTaxSummaryRemote(response?.data?.data || response?.data || null);
    } catch (error) {
      // ok if not implemented
      console.warn("Summary fetch failed:", error);
      setPercentageTaxSummaryRemote(null);
    }
  }, [client?._id, activeSection]);

  /* ============================
      FETCH: 2306 (FY + Quarter; month optional)
      - IMPORTANT: Your VATManagement already uses:
        formAPI.getForm2306ByClient(clientId, params)
  ============================ */
  const fetchForm2306Records = useCallback(async () => {
    if (!client?._id) {
      setForm2306Records([]);
      return;
    }

    try {
      const params = {
        limit: 2000,
        page: 1,
      };

      if (selectedPeriod.year) {
        params.fiscalYear = selectedPeriod.year;
      }

      if (selectedPeriod.quarter) {
        params.quarter = selectedPeriod.quarter;
      }

      const res = await formAPI.getForm2306ByClient(client._id, params);
      const records = res?.data?.records || res?.data?.data?.records || [];
      setForm2306Records(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error("Error fetching Form 2306 records:", err);
      setForm2306Records([]);
    }
  }, [client?._id, selectedPeriod.year, selectedPeriod.quarter]);

  /* ============================
      FETCH: 2307 (FY + Quarter)
      Read-only — certificates are derived at posting time, not encoded here.
  ============================ */
  const fetchForm2307Records = useCallback(async () => {
    if (!client?._id || !selectedPeriod.year) {
      setForm2307Records([]);
      return;
    }

    try {
      setLoading2307(true);
      const res = await form2307API.list(client._id, {
        fiscalYear: selectedPeriod.year,
        ...(selectedPeriod.quarter ? { quarter: selectedPeriod.quarter } : {}),
      });
      setForm2307Records(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching Form 2307 records:", err);
      setForm2307Records([]);
    } finally {
      setLoading2307(false);
    }
  }, [client?._id, selectedPeriod.year, selectedPeriod.quarter]);

  const fetchExcessAdjustment = useCallback(async () => {
    if (!client?._id || !selectedPeriod.year || !selectedPeriod.quarter) {
      setExcessFromPrevQuarter(0);
      return;
    }

    try {
      const response = await percentageTaxAPI.getQuarterAdjustment(client._id, {
        quarter: selectedPeriod.quarter,
        fiscalYear: selectedPeriod.year,
      });
      setExcessFromPrevQuarter(Number(response?.data?.excessFromPrevQuarter) || 0);
    } catch (err) {
      console.error('Error fetching Percentage Tax excess adjustment:', err);
      setExcessFromPrevQuarter(0);
    }
  }, [client?._id, selectedPeriod.year, selectedPeriod.quarter]);

  /* ============================
      LOAD on tab changes
  ============================ */
  useEffect(() => {
    // update percent tax whenever tab changes (exclude "excess" since its own component handles data)
    if (['sales', 'income', 'records', 'summary'].includes(activeSection)) {
      fetchPercentageTaxRecords();
      fetchPercentageTaxSummary();
    }

    // load 2306 when needed (still fetch for summary/records)
    if (activeSection === "2306" || activeSection === "records" || activeSection === "summary") {
      fetchForm2306Records();
      fetchForm2307Records();
    }

    if (activeSection === 'records' || activeSection === 'summary' || activeSection === 'excess') {
      fetchExcessAdjustment();
    }
  }, [
    activeSection,
    fetchPercentageTaxRecords,
    fetchPercentageTaxSummary,
    fetchForm2306Records,
    fetchForm2307Records,
    fetchExcessAdjustment,
  ]);

  /* ============================
      Auto-select current period on first load
  ============================ */
  useEffect(() => {
    if (!selectedPeriod.quarter || !selectedPeriod.year) {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const quarter = calculateQuarterFromMonth(month);
      setSelectedPeriod({ quarter, year, month });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============================
      Handlers: Add / Delete / Update
      IMPORTANT:
      - Backend routes:
        PUT    /percentage-tax/:recordId
        DELETE /percentage-tax/:recordId
      So API methods should be:
        updateRecord(recordId, payload)
        deleteRecord(recordId)
  ============================ */
  const autoPostNewRecordToGL = useCallback(
    async (record) => {
      if (!client?._id || !record?._id || !record?.quarter || !record?.fiscalYear) {
        return false;
      }

      try {
        await axiosInstance.post(`/percentage-tax/clients/${client._id}/post-record-to-gl`, {
          recordId: record._id,
          accountingClientId: client._id,
        });

        await fetchPercentageTaxRecords();
        await fetchPercentageTaxSummary();
        return true;
      } catch (error) {
        console.error('Auto-post to GL failed for Percentage Tax record:', error);
        return false;
      }
    },
    [client?._id, fetchPercentageTaxRecords, fetchPercentageTaxSummary]
  );

  const handlePercentageTaxAdded = useCallback(
    async (savedRecord) => {
      const refreshedByAutoPost = await autoPostNewRecordToGL(savedRecord);
      if (!refreshedByAutoPost) {
        await fetchPercentageTaxRecords();
        await fetchPercentageTaxSummary();
      }
    },
    [autoPostNewRecordToGL, fetchPercentageTaxRecords, fetchPercentageTaxSummary]
  );

  const handle2306RecordAdded = useCallback(async () => {
    await fetchForm2306Records();
  }, [fetchForm2306Records]);

  const handlePercentageTaxDeleted = useCallback(
    async (recordId) => {
      try {
        // ✅ recordId only (NOT clientId)
        await percentageTaxAPI.deleteRecord(recordId);
        await fetchPercentageTaxRecords();
        await fetchPercentageTaxSummary();
      } catch (error) {
        console.error("Delete failed:", error);
        notify.error("Couldn't delete", error?.response?.data?.message || error?.message || "Unknown error — try again.");
      }
    },
    [fetchPercentageTaxRecords, fetchPercentageTaxSummary]
  );

  const [pendingForm2306Delete, setPendingForm2306Delete] = useState(null);
  const [deletingForm2306, setDeletingForm2306] = useState(false);

  const handleForm2306Deleted = useCallback((recordId) => {
    setPendingForm2306Delete(recordId);
  }, []);

  const confirmForm2306Delete = useCallback(async () => {
    const recordId = pendingForm2306Delete;
    if (!recordId) return;

    try {
      setDeletingForm2306(true);
      await formAPI.deleteForm2306Record(recordId);
      await fetchForm2306Records();
      await fetchPercentageTaxSummary();
    } catch (error) {
      console.error("Delete Form 2306 failed:", error);
      notify.error("Couldn't delete", error?.message || "Unknown error — try again.");
    } finally {
      setDeletingForm2306(false);
      setPendingForm2306Delete(null);
    }
  }, [pendingForm2306Delete, fetchForm2306Records, fetchPercentageTaxSummary]);

  const handlePercentageTaxEditSave = useCallback(
    async (updated) => {
      try {
        if (!updated?._id) throw new Error("Missing record ID.");

        /**
         * IMPORTANT BACKEND MISMATCH NOTE:
         * Your router.put allowedUpdates includes: grossSales, taxRate, taxDue...
         * but your POST creates fields: amount, taxRate, taxDue...
         *
         * To make update work WITHOUT changing backend right now,
         * we send BOTH:
         * - grossSales (from amount)
         * - amount (keep your simple model consistent)
         */
        const payload = {
          month: updated.month,
          category: updated.category, // allow changing sales/income
          chartOfAccount: updated.chartOfAccount,
          chartOfAccountName: updated.chartOfAccountName,
          taxRate: Number(updated.taxRate) || 3,
          taxDue: Number(updated.taxDue) || 0,
          // send both models
          amount: Number(updated.amount) || 0,
          grossSales: Number(updated.amount) || 0,
          remarks: updated.remarks || '',
          description: updated.description || '',
          transactionDate: updated.transactionDate,
          status: updated.status,
        };

        console.log('[PT Edit Save] payload being sent:', payload);

        await percentageTaxAPI.updateRecord(updated._id, payload);

        if (updated?.isPostedToGL && updated?.glJournalEntry && client?._id) {
          try {
            await percentageTaxAPI.adjustGLRecord(client._id, {
              recordId: updated._id,
              accountingClientId: client._id,
            });
          } catch (adjustError) {
            console.error("Auto-adjust Percentage Tax GL record failed:", adjustError);
          }
        }

        closeEditModal();
        await fetchPercentageTaxRecords();
        await fetchPercentageTaxSummary();
      } catch (error) {
        console.error("Update failed:", error);
        notify.error("Couldn't save", error?.response?.data?.message || error?.message || "Unknown error — try again.");
      }
    },
    [client?._id, fetchPercentageTaxRecords, fetchPercentageTaxSummary]
  );

  /* ============================
      Period filter (Percent tax)
      - percent tax records are stored with quarter + fiscalYear (and month)
  ============================ */
  const getFilteredPercentageTaxRecords = useCallback(() => {
    const { quarter, year } = selectedPeriod;

    if (quarter && year) {
      return percentageTaxRecords.filter((r) => r.quarter === quarter && r.fiscalYear === year);
    }
    if (year && !quarter) return percentageTaxRecords.filter((r) => r.fiscalYear === year);
    if (quarter && !year) return percentageTaxRecords.filter((r) => r.quarter === quarter);

    return percentageTaxRecords;
  }, [percentageTaxRecords, selectedPeriod]);

  const recordsMonth = selectedPeriod.month ?? null;
  const summaryMonth = selectedPeriod.month ?? null;

  const filtered2306BySelectedPeriod = useMemo(() => {
    const selectedYear = selectedPeriod.year;
    const selectedQuarter = selectedPeriod.quarter;

    return (form2306Records || []).filter((record) => {
      const recordYear = Number(record.fiscalYear);
      const recordQuarter = Number(record.quarter) || calculateQuarterFromMonth(record.month);

      const yearMatch = !selectedYear || recordYear === Number(selectedYear);
      const quarterMatch = !selectedQuarter || recordQuarter === Number(selectedQuarter);

      return yearMatch && quarterMatch;
    });
  }, [form2306Records, selectedPeriod.year, selectedPeriod.quarter]);

  const filtered2306ForRecords = useMemo(() => {
    return filtered2306BySelectedPeriod.filter(
      (record) => recordsMonth === null || Number(record.month) === recordsMonth
    );
  }, [filtered2306BySelectedPeriod, recordsMonth]);

  // Same period narrowing the 2306 list uses, so both tables in the Records
  // section always describe the same period.
  const filtered2307BySelectedPeriod = useMemo(() => {
    const selectedYear = selectedPeriod.year;
    const selectedQuarter = selectedPeriod.quarter;

    return (form2307Records || []).filter((record) => {
      const recordYear = Number(record.fiscalYear);
      const recordQuarter = Number(record.quarter) || calculateQuarterFromMonth(record.month);

      const yearMatch = !selectedYear || recordYear === Number(selectedYear);
      const quarterMatch = !selectedQuarter || recordQuarter === Number(selectedQuarter);

      return yearMatch && quarterMatch;
    });
  }, [form2307Records, selectedPeriod.year, selectedPeriod.quarter]);

  const filtered2307ForRecords = useMemo(() => {
    return filtered2307BySelectedPeriod.filter(
      (record) => recordsMonth === null || Number(record.month) === recordsMonth
    );
  }, [filtered2307BySelectedPeriod, recordsMonth]);

  const filtered2306ForSummary = useMemo(() => {
    return filtered2306BySelectedPeriod.filter(
      (record) => summaryMonth === null || Number(record.month) === summaryMonth
    );
  }, [filtered2306BySelectedPeriod, summaryMonth]);

  /* ============================
      Summary calc (Percent Tax + 2306)
      Tax Due (from % tax) - 2306 withheld = Net Tax Payable
  ============================ */
  const periodSummary = useMemo(() => {
    const filtered = getFilteredPercentageTaxRecords();

    const totalAmount = filtered.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
    const salesTaxDue = filtered
      .filter((r) => r.category === 'sales')
      .reduce((acc, r) => acc + (Number(r.taxDue) || 0), 0);
    const incomeTaxDue = filtered
      .filter((r) => r.category === 'income')
      .reduce((acc, r) => acc + (Number(r.taxDue) || 0), 0);
    const totalTaxDue = salesTaxDue + incomeTaxDue;
    const recordCount = filtered.length;

    const total2306 = filtered2306ForSummary.reduce(
      (acc, r) => acc + (Number(r.taxWithheld) || 0),
      0
    );

    // payable = sales - income - 2306 - excessFromPrevQuarter
    const netTaxPayableRaw = salesTaxDue - incomeTaxDue - total2306 - (Number(excessFromPrevQuarter) || 0);
    const netTaxPayable = Math.max(0, netTaxPayableRaw);
    const excessCredit = netTaxPayableRaw < 0 ? Math.abs(netTaxPayableRaw) : 0;

    return {
      totalAmount,
      totalTaxDue,
      salesTaxDue,
      incomeTaxDue,
      recordCount,
      total2306,
      excessFromPrevQuarter: Number(excessFromPrevQuarter) || 0,
      netTaxPayable,
      excessCredit,
      form2306Count: filtered2306ForSummary.length,
    };
  }, [getFilteredPercentageTaxRecords, filtered2306ForSummary, excessFromPrevQuarter]);

  const records2306Total = useMemo(
    () => filtered2306ForRecords.reduce((acc, r) => acc + (Number(r.taxWithheld) || 0), 0),
    [filtered2306ForRecords]
  );

  // SAWT credits claimed on the 2551Q: percentage tax withheld at source, i.e.
  // non-voided WB 2307s in the summary period. Deliberately separate from
  // periodSummary.total2306, which is a term in the net-payable formula and
  // waits on accountant open item 6 — this figure only feeds the FilingCard.
  const sawtCredits2307 = useMemo(
    () => filtered2307BySelectedPeriod
      .filter((r) => summaryMonth === null || Number(r.month) === summaryMonth)
      .filter((r) => !r.isVoided && isPercentageTaxWithholdingAtc(r.atcCode))
      .reduce((acc, r) => acc + (Number(r.taxWithheld) || 0), 0),
    [filtered2307BySelectedPeriod, summaryMonth]
  );

  const exportSawtDat = useCallback(() => {
    // SAWT is built from creditable 2307s only — 2306 is final tax and is never
    // a credit. Voided certificates are not credits either.
    const certificates = filtered2307ForRecords.filter((r) => !r.isVoided);
    if (!certificates.length) return;

    const payeeOf = (r) => ({
      name: r.payeeName || r.customer?.name || r.vendor?.name || r.vendorName || '',
      tin: r.payerTIN || r.customer?.tin || r.vendor?.tin || '',
    });

    // A blank ATC cannot be routed to the percentage-tax or the income-tax
    // side, so it blocks the file. Never default it.
    const missingATCs = findMissingATCs(certificates.map((r) => ({
      name: payeeOf(r).name,
      atcCode: r.atcCode,
      certificateNumber: r.certificateNumber,
    })));
    if (missingATCs.length) { notify.error("Couldn't export", missingATCMessage(missingATCs)); return; }

    // A 2551Q SAWT lists only percentage tax withheld at source (WB).
    // Expanded-withholding 2307s are income-tax credits and belong on the
    // 1701Q/1702Q SAWT.
    const form2307 = certificates.filter((r) => isPercentageTaxWithholdingAtc(r.atcCode));
    if (!form2307.length) {
      notify.error(
        "Couldn't export",
        'None of these Form 2307 records carry a percentage-tax withholding ATC (WB). '
          + 'A 2551Q SAWT lists only percentage tax withheld at source; expanded-withholding '
          + '2307s belong on the income tax SAWT.'
      );
      return;
    }

    const clientTIN9 = cleanTIN(client?.tin);
    if (!clientTIN9) { notify.error("Couldn't export", 'Client TIN is required for DAT export.'); return; }

    // A missing payee TIN must block the file, never be padded into 000000000.
    const missingTINs = findMissingTINs(form2307.map(payeeOf));
    if (missingTINs.length) { notify.error("Couldn't export", missingTINMessage(missingTINs)); return; }

    // FTYPE_CODE is the filer's entity type (1701/1702), never a return code.
    const ftypeCode = resolveFtypeCode(client?.clientType);
    if (!ftypeCode) { notify.error("Couldn't export", missingFtypeMessage(client?.name)); return; }

    const rdoCode = filerRdoCode(client);
    if (!rdoCode) { notify.error("Couldn't export", missingRdoMessage(client?.name)); return; }
    // Header period = latest record's month (passed through; period selection
    // is a separate task). Each detail row keeps its own record period.
    const sorted = [...form2307].sort((a, b) => {
      if ((a.fiscalYear || 0) !== (b.fiscalYear || 0)) return (a.fiscalYear || 0) - (b.fiscalYear || 0);
      return (a.month || 0) - (b.month || 0);
    });
    const last = sorted[sorted.length - 1];
    const exportMonth = last?.month || new Date().getMonth() + 1;
    const exportYear = last?.fiscalYear || new Date().getFullYear();

    const result = buildSawtDat({
      filer: {
        tin: client?.tin,
        branchCode: datFilerBranch(client?.branchCode),
        rdoCode,
        clientType: client?.clientType,
        name: client?.name,
      },
      period: { month: exportMonth, year: exportYear },
      rows: sorted.map((r) => {
        const payee = payeeOf(r);
        return {
          tin: payee.tin,
          branchCode: '0000',
          // A 2307's payor (the withholding agent) is always stored on `vendor`
          // (withholdingCertificate.service.js resolveCounterpartyVendor), so its
          // entity type is Vendor.businessType — not customer.businessType.
          payeeEntity: isCorporatePayee(r.vendor?.businessType) ? 'corporation' : 'individual',
          name: payee.name,
          period: { month: r.month || exportMonth, year: r.fiscalYear || exportYear },
          atcCode: r.atcCode,
          // Annex A TAX_RATE is 9(2).99 — withholdingRate is already a whole
          // percent (the on-screen rate column renders it with a % sign).
          ratePercent: Number(r.withholdingRate || 0),
          baseAmount: Number(r.baseAmount || 0),
          taxWithheld: Number(r.taxWithheld || 0),
        };
      }),
    });
    if (!result.ok) { notify.error("Couldn't export", result.errors.join(' ')); return; }
    downloadSawtDat(result);

    const totalWithheld = sorted.reduce((sum, r) => sum + Number(r.taxWithheld || 0), 0);

    // File is on disk — only now is the attachment genuinely "exported".
    // totalWithheld is the sum of exactly the WB 2307 rows written above. It can
    // legitimately differ from sawtCredits2307 when the Records tab
    // month filter is narrower than the Summary tab's; snapshotting what the
    // file actually contained is the point, and the filing card surfaces the
    // gap as a mismatch rather than hiding it.
    recordAttachmentExport({
      clientId: client?._id,
      formCode: '2551Q',
      fiscalYear: selectedPeriod.year,
      quarter: selectedPeriod.quarter,
      type: ATTACHMENT_TYPES.SAWT,
      total: totalWithheld,
    });
  }, [filtered2307ForRecords, client, selectedPeriod.year, selectedPeriod.quarter]);

  const recordsNetTaxPayableRaw =
    periodSummary.salesTaxDue -
    periodSummary.incomeTaxDue -
    records2306Total -
    (Number(periodSummary.excessFromPrevQuarter) || 0);
  const recordsNetTaxPayable = Math.max(0, recordsNetTaxPayableRaw);
  const recordsExcessCredit = recordsNetTaxPayableRaw < 0 ? Math.abs(recordsNetTaxPayableRaw) : 0;
  const isAllPeriods = !selectedPeriod.quarter && !selectedPeriod.year;

  const recordsPeriodTitle = useMemo(() => {
    if (isAllPeriods) return "Records (All Periods)";
    if (selectedPeriod.year && !selectedPeriod.quarter) {
      return `Records (All Quarters ${selectedPeriod.year})`;
    }
    if (selectedPeriod.year && selectedPeriod.quarter) {
      return `Records (Q${selectedPeriod.quarter} ${selectedPeriod.year})`;
    }
    if (!selectedPeriod.year && selectedPeriod.quarter) {
      return `Records (Q${selectedPeriod.quarter} All Years)`;
    }
    return "Records";
  }, [isAllPeriods, selectedPeriod.year, selectedPeriod.quarter]);

  const annualSummary = useMemo(() => {
    const summaryByYear = {};

    percentageTaxRecords.forEach((record) => {
      const year = record.fiscalYear;
      if (!year) return;

      if (!summaryByYear[year]) {
        summaryByYear[year] = {
          year,
          totalAmount: 0,
          totalTaxDue: 0,
          recordCount: 0,
          quarters: { 1: 0, 2: 0, 3: 0, 4: 0 },
        };
      }

      summaryByYear[year].totalAmount += Number(record.amount) || 0;
      summaryByYear[year].totalTaxDue += Number(record.taxDue) || 0;
      summaryByYear[year].recordCount += 1;

      if (record.quarter) {
        summaryByYear[year].quarters[record.quarter] += Number(record.taxDue) || 0;
      }
    });

    return Object.values(summaryByYear).sort((a, b) => b.year - a.year);
  }, [percentageTaxRecords]);


  const sections = useMemo(
    () => [
      { id: "sales", name: "Sales", description: "Encode sales percentage tax" },
      { id: "income", name: "Expenses", description: "Encode income percentage tax" },
      { id: "2306", name: "Form 2306", description: "Encode withheld tax (to deduct from % tax due)" },
      { id: "records", name: "Records", description: "View/edit/delete all records (+ 2306)" },
      { id: "excess", name: "Excess", description: "Adjust excess from previous quarter" },
      { id: "summary", name: "Summary", description: "Tax Due − 2306 = Net Payable" },
    ],
    []
  );

  if (loading && percentageTaxRecords.length === 0 && !["sales","income","2306","excess"].includes(activeSection)) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
          <p className="mt-4 text-ink-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-ink-line pb-4">
        <div className="flex justify-between items-start gap-6 flex-wrap">
          <div>
            <h2 className="text-xl font-medium text-ink">
              Percentage Tax (BIR Form 2551Q)
              {(activeSection === 'sales' || activeSection === 'income') && (
                <span className="text-primary-500"> &middot; {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}</span>
              )}
            </h2>
            <p className="text-ink-body mt-1">
              Month-based entry (auto-groups to quarter) for <span className="font-medium text-ink">{client?.name}</span>
            </p>
          </div>

          {/* Period Selector */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-ink-body">Period:</span>

            <select
              value={selectedPeriod.quarter || ""}
              onChange={(e) => {
                const quarter = e.target.value ? parseInt(e.target.value, 10) : null;
                setSelectedPeriod((prev) => {
                  // Drop the month if it no longer belongs to the newly selected quarter
                  const monthStillValid =
                    quarter && prev.month
                      ? QUARTER_MONTHS[quarter].some((m) => m.value === prev.month)
                      : !prev.month;
                  return { ...prev, quarter, month: monthStillValid ? prev.month : null };
                });
              }}
              className="block rounded-md border-ink-line py-1.5 pl-3 pr-10 text-sm focus:border-primary-500 focus:ring-primary-500"
              disabled={loading}
            >
              <option value="">All Quarters</option>
              {quarters.map((q) => (
                <option key={q.value} value={q.value}>
                  {q.label}
                </option>
              ))}
            </select>

            <select
              value={selectedPeriod.year || ""}
              onChange={(e) => {
                const year = e.target.value ? parseInt(e.target.value, 10) : null;
                setSelectedPeriod((prev) => ({ ...prev, year }));
              }}
              className="block rounded-md border-ink-line py-1.5 pl-3 pr-10 text-sm focus:border-primary-500 focus:ring-primary-500"
              disabled={loading}
            >
              <option value="">All Years</option>
              {getAvailableYears().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            {/* Month (optional; mostly for 2306 form POST) — scoped to the
                selected quarter's 3 months, matching VAT Management */}
            <select
              value={selectedPeriod.month || ""}
              onChange={(e) => {
                const month = e.target.value ? parseInt(e.target.value, 10) : null;
                setSelectedPeriod((prev) => ({ ...prev, month }));
              }}
              className="block rounded-md border-ink-line py-1.5 pl-3 pr-10 text-sm focus:border-primary-500 focus:ring-primary-500"
              disabled={loading}
              title="Optional: used mainly for 2306 encoding / month viewing"
            >
              <option value="">
                {selectedPeriod.quarter ? "All Months (Full Quarter)" : "All Months"}
              </option>
              {(selectedPeriod.quarter ? QUARTER_MONTHS[selectedPeriod.quarter] : MONTH_OPTIONS).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            {(selectedPeriod.quarter || selectedPeriod.year || selectedPeriod.month) && (
              <button
                type="button"
                onClick={() => setSelectedPeriod({ quarter: null, year: null, month: null })}
                className="text-xs text-primary-500 hover:text-primary-700"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="border-b border-ink-line">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              disabled={loading}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSection === section.id
                  ? "border-primary-500 text-primary-500"
                  : "border-transparent text-ink-body hover:text-ink-body hover:border-ink-line disabled:opacity-50"
              }`}
              title={section.description}
            >
              {section.name}
              {loading && activeSection === section.id && (
                <span className="ml-2 inline-block animate-pulse">↻</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg border">
        {/* Encode (sales/income) */}
        {(activeSection === "sales" || activeSection === "income") && (
          <PercentageTaxForm
            client={client}
            onRecordAdded={handlePercentageTaxAdded}
            selectedYear={selectedPeriod.year}
            category={activeSection}
          />
        )}

        {/* 2306 Tab */}
        {activeSection === "2306" && (
          <Form2306Form
            client={client}
            selectedQuarter={selectedPeriod.quarter}
            selectedYear={selectedPeriod.year}
            selectedMonth={selectedPeriod.month} // required by your 2306 form for POST
            onRecordAdded={handle2306RecordAdded}
          />
        )}

        {/* Excess / Quarter Summary Tab */}
        {activeSection === "excess" && (
          <div className="p-6">
            <PercentageTaxQuarterSummary
              clientId={client?._id}
              initialQuarter={selectedPeriod.quarter}
              initialFiscalYear={selectedPeriod.year}
            />
          </div>
        )}

        {/* Records */}
        {activeSection === "records" && (
          <div className="p-6 space-y-6">
            {/* Top Summary Strip */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h4 className="text-lg font-medium text-ink">
                  {recordsPeriodTitle}
                </h4>

                <div className="text-sm text-ink-body bg-ink-canvas px-3 py-1 rounded-md">
                  {periodSummary.recordCount} %tax record(s)
                </div>

                <div className="text-sm text-ink-body bg-ink-canvas px-3 py-1 rounded-md">
                  {filtered2306ForRecords.length} 2306 row(s)
                </div>
              </div>

              <div className="text-sm text-ink-body space-y-1">
                <div>
                  Tax Due (% tax):{" "}
                  <span className="font-medium text-ink tabular-nums">{formatCurrency(periodSummary.totalTaxDue)}</span>
                </div>
                <div>
                  Less: 2306 Withheld:{" "}
                  <span className="font-medium text-ink tabular-nums">{formatCurrency(records2306Total)}</span>
                </div>
                <div>
                  Less: Excess Prev Quarter:{" "}
                  <span className="font-medium text-ink tabular-nums">{formatCurrency(periodSummary.excessFromPrevQuarter)}</span>
                </div>
                <div>
                  Net Tax Payable:{" "}
                  <span className="font-medium text-primary-700 tabular-nums">
                    {formatCurrency(recordsNetTaxPayable)}
                  </span>
                  {recordsExcessCredit > 0 && (
                    <span className="ml-2 text-xs text-ink-body">
                      (Excess credit: {formatCurrency(recordsExcessCredit)})
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Percentage Tax List */}
            <div className="border border-ink-line rounded-lg">
              <div className="px-4 py-3 border-b border-ink-line bg-ink-canvas">
                <p className="font-medium text-ink">% Tax (2551Q) records</p>
                <p className="text-xs text-ink-body">Edit or delete percentage tax rows</p>
              </div>

              <PercentageTaxList
                records={
                  isAllPeriods
                    ? percentageTaxRecords
                    : percentageTaxRecords.filter(
                        (r) => r.quarter === selectedPeriod.quarter && r.fiscalYear === selectedPeriod.year
                      )
                }
                onRecordDeleted={handlePercentageTaxDeleted}
                onRecordUpdated={openEditModal}
                showActions={true}
                clientId={client?._id}
                client={client}
              />
            </div>

            {/* 2306 list (simple inline table so you don't need a whole management component) */}
            <div className="border border-ink-line rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-ink-line bg-ink-canvas flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-ink">Form 2306 (withheld) — included in net tax payable</p>
                  <p className="text-xs text-ink-body">
                    Total withheld is deducted from % tax due in Summary & Records.
                  </p>
                </div>
                <Button
                  onClick={exportSawtDat}
                  disabled={filtered2307ForRecords.filter((r) => !r.isVoided).length === 0 || !client?.tin}
                  variant="outline"
                  size="sm"
                  icon={Download}
                  title="Export percentage-tax-withholding Form 2307 records as a SAWT DAT file"
                  className="whitespace-nowrap"
                >
                  2551M DAT
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white">
                    <tr className="text-left text-ink-body border-b border-ink-line">
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Period</th>
                      <th className="px-4 py-2">Description</th>
                      <th className="px-4 py-2">Customer</th>
                      <th className="px-4 py-2 text-right">Base</th>
                      <th className="px-4 py-2 text-right">Withheld</th>
                      <th className="px-4 py-2 text-right">Rate</th>
                      <th className="px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered2306ForRecords.length === 0 ? (
                      <tr>
                        <td className="px-4 py-4 text-ink-body" colSpan={8}>
                          No 2306 records for the selected period.
                        </td>
                      </tr>
                    ) : (
                      filtered2306ForRecords.map((r) => (
                        <tr key={r._id} className="border-b border-ink-line last:border-b-0">
                          <td className="px-4 py-2">
                            {r.transactionDate ? new Date(r.transactionDate).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-2">
                            <div className="text-ink">Q{Number(r.quarter) || calculateQuarterFromMonth(r.month)}</div>
                            <div className="text-xs text-ink-body">
                              {MONTH_OPTIONS.find((m) => m.value === Number(r.month))?.label || "—"} {r.fiscalYear || ""}
                            </div>
                          </td>
                          <td className="px-4 py-2">{r.description || "—"}</td>
                          <td className="px-4 py-2">
                            {r.payeeName || r.customer?.name || r.vendor?.name || r.vendorName || "—"}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {formatCurrency(Number(r.baseAmount) || 0)}
                          </td>
                          <td className="px-4 py-2 text-right font-medium text-ink tabular-nums">
                            {formatCurrency(Number(r.taxWithheld) || 0)}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {Number(r.withholdingRate ?? 0).toFixed(2)}%
                          </td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() => handleForm2306Deleted(r._id)}
                              className="text-danger-500 hover:text-danger-700"
                              title="Delete record"
                              aria-label="Delete record"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-3 bg-white text-xs text-ink-body">
                Formula: <span className="font-mono">Net Tax Payable = %Tax Due − 2306 Withheld</span>
              </div>
            </div>

            {/*
              2307 list, alongside the 2306 one above so a preparer sees every
              certificate for the period without leaving the Records tab.

              Read-only: these are derived automatically when a transaction with
              an ATC is posted, and an auto-generated certificate is immutable.

              NOTE the footnote — these are DISPLAYED here, not yet deducted from
              net tax payable. The formula above still credits 2306 only.
            */}
            <Form2307RecordsTable
              clientId={client?._id}
              records={filtered2307ForRecords}
              loading={loading2307}
              subtitle="Generated automatically when a transaction with an ATC is posted. Shown for reference."
              emptyHint="No 2307 records for the selected period."
              footnote="Shown for reference — not deducted from net tax payable above, which credits Form 2306 only."
            />
          </div>
        )}

        {/* Summary */}
        {activeSection === "summary" && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-medium text-ink">Percentage tax summary</h3>
              <p className="text-sm text-ink-body mt-1">
                Tax due (from % tax) − 2306 withheld = net tax payable
              </p>
            </div>

            {/* Summary Cards — neutral inputs, one highlighted answer */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                <div className="text-xs text-ink-body">Total amount</div>
                <div className="text-lg font-medium text-ink tabular-nums">{formatCurrency(periodSummary.totalAmount)}</div>
                <div className="text-xs text-ink-body">{periodSummary.recordCount} %tax record(s)</div>
              </div>

              <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                <div className="text-xs text-ink-body">Tax due (% tax)</div>
                <div className="text-lg font-medium text-ink tabular-nums">{formatCurrency(periodSummary.totalTaxDue)}</div>
              </div>

              <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                <div className="text-xs text-ink-body">Less: 2306 withheld</div>
                <div className="text-lg font-medium text-ink tabular-nums">{formatCurrency(periodSummary.total2306)}</div>
                <div className="text-xs text-ink-body">{periodSummary.form2306Count} row(s)</div>
              </div>

              <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                <div className="text-xs text-ink-body">Less: excess prev quarter</div>
                <div className="text-lg font-medium text-ink tabular-nums">{formatCurrency(periodSummary.excessFromPrevQuarter)}</div>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                <div className="text-xs text-primary-700">Net tax payable</div>
                <div className="text-lg font-medium text-primary-700 tabular-nums">{formatCurrency(periodSummary.netTaxPayable)}</div>
                {periodSummary.excessCredit > 0 && (
                  <div className="text-xs text-primary-700/70">
                    Excess credit: {formatCurrency(periodSummary.excessCredit)}
                  </div>
                )}
              </div>
            </div>

            <div className="text-xs text-ink-body font-mono">
              Formula: Net Tax Payable = %Tax Due − 2306 Withheld − Excess Prev Quarter
            </div>

            {selectedPeriod.quarter && selectedPeriod.year && (
              <FilingCard
                client={client}
                formCode="2551Q"
                fiscalYear={selectedPeriod.year}
                quarter={selectedPeriod.quarter}
                creditsClaimed={sawtCredits2307}
                hasSlsp={false}
                onGoToRecords={() => setActiveSection("records")}
                onReviewAttachment={() => setActiveSection("records")}
              />
            )}

            {/* Optional: keep your existing summary component (if it renders charts/annual tables) */}
            {percentageTaxRecords.length > 0 && PercentageTaxSummary ? (
              <div className="mt-4">
                <PercentageTaxSummary
                  client={client}
                  records={percentageTaxRecords}
                  selectedPeriod={{ quarter: selectedPeriod.quarter, year: selectedPeriod.year }}
                  periodSummary={periodSummary}
                  annualSummary={annualSummary}
                  remoteSummary={percentageTaxSummaryRemote}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingRecord && (
        <PercentageTaxEditModal
          record={editingRecord}
          onClose={closeEditModal}
          onSave={handlePercentageTaxEditSave}
        />
      )}

      <ConfirmModal
        isOpen={!!pendingForm2306Delete}
        title="Delete this Form 2306 record?"
        message="This can't be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deletingForm2306}
        onConfirm={confirmForm2306Delete}
        onCancel={() => setPendingForm2306Delete(null)}
      />
    </div>
  );
};

export default PercentageTaxFormManagement;